/// <reference types="@types/google.maps" />
/* global google */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { GoogleType } from './types.js'

const LINE_COLOR = '#1E40AF'
const MARKER_FILL = '#1E40AF'
const DARK_LINE_COLOR = '#38BDF8'
const DARK_MARKER_FILL = '#0284C7'

function formatDistance(meters: number): string {
  if (meters >= 1000) return (meters / 1000).toFixed(2) + ' km'
  return meters.toFixed(1) + ' m'
}

export type MeasureTool = {
  active: boolean
  totalMeters: number
  pointCount: number
  toggle: () => void
  exit: () => void
  clear: () => void
}

/** Multi-point distance measurement on a Google Map. While active, each map
 *  click drops a numbered marker and a polyline is drawn between successive
 *  points. Esc or toggling exits the mode but preserves the drawn shape until
 *  `clear()` is called. */
export function useMeasureTool(g: GoogleType | null, map: google.maps.Map | null, isDarkMode = false): MeasureTool {
  const [active, setActive] = useState(false)
  const [points, setPoints] = useState<google.maps.LatLngLiteral[]>([])
  const lineColor = isDarkMode ? DARK_LINE_COLOR : LINE_COLOR
  const markerFill = isDarkMode ? DARK_MARKER_FILL : MARKER_FILL

  const markersRef = useRef<google.maps.Marker[]>([])
  const polylineRef = useRef<google.maps.Polyline | null>(null)
  const segLabelsRef = useRef<google.maps.Marker[]>([])

  // Total distance is derived; recompute on every render — cheap for the
  // small number of points a user will click.
  let totalMeters = 0
  if (g && points.length >= 2) {
    for (let i = 1; i < points.length; i++) {
      totalMeters += g.maps.geometry.spherical.computeDistanceBetween(
        new g.maps.LatLng(points[i - 1]!.lat, points[i - 1]!.lng),
        new g.maps.LatLng(points[i]!.lat, points[i]!.lng),
      )
    }
  }

  // Redraw markers + polyline + segment labels whenever points change.
  useEffect(() => {
    if (!g || !map) return

    markersRef.current.forEach((m) => m.setMap(null))
    markersRef.current.length = 0
    segLabelsRef.current.forEach((l) => l.setMap(null))
    segLabelsRef.current.length = 0
    polylineRef.current?.setMap(null)
    polylineRef.current = null

    if (points.length === 0) return

    if (points.length >= 2) {
      polylineRef.current = new g.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: lineColor,
        strokeOpacity: 0.9,
        strokeWeight: 3,
        map,
        clickable: false,
        zIndex: 4000,
      })
    }

    points.forEach((p, i) => {
      markersRef.current.push(
        new g.maps.Marker({
          position: p,
          map,
          label: { text: String(i + 1), color: '#fff', fontSize: '11px', fontWeight: '700' },
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: markerFill,
            fillOpacity: 1,
            strokeColor: isDarkMode ? '#0f172a' : '#fff',
            strokeWeight: 2,
          },
          zIndex: 4500 + i,
          clickable: false,
        }),
      )
    })

    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1]!
      const b = points[i]!
      const segDist = g.maps.geometry.spherical.computeDistanceBetween(
        new g.maps.LatLng(a.lat, a.lng),
        new g.maps.LatLng(b.lat, b.lng),
      )
      const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 }
      segLabelsRef.current.push(
        new g.maps.Marker({
          position: mid,
          map,
          icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0 },
          label: {
            text: formatDistance(segDist),
            fontSize: '11px',
            color: lineColor,
            fontWeight: '700',
            className: 'measure-seg-label',
          },
          zIndex: 4400,
          clickable: false,
        }),
      )
    }
  }, [g, map, points, isDarkMode, lineColor, markerFill])

  // Wire/un-wire the map click listener and crosshair cursor based on active.
  useEffect(() => {
    if (!g || !map) return
    if (!active) return

    map.setOptions({ draggableCursor: 'crosshair' })
    const listener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()
      setPoints((prev) => [...prev, { lat, lng }])
    })

    return () => {
      listener.remove()
      map.setOptions({ draggableCursor: null })
    }
  }, [g, map, active])

  // Esc to exit measure mode (preserves current drawing).
  useEffect(() => {
    if (!active) return
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setActive(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [active])

  // Cleanup on unmount: tear down all overlays.
  useEffect(() => {
    const markers = markersRef
    const labels = segLabelsRef
    const polyline = polylineRef
    return () => {
      markers.current.forEach((m) => m.setMap(null))
      labels.current.forEach((l) => l.setMap(null))
      polyline.current?.setMap(null)
    }
  }, [])

  const toggle = useCallback(() => setActive((a) => !a), [])
  const exit = useCallback(() => setActive(false), [])
  const clear = useCallback(() => setPoints([]), [])

  return { active, totalMeters, pointCount: points.length, toggle, exit, clear }
}

export { formatDistance as formatMeasureDistance }
